import {
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Download,
    Eye,
    FileUp,
    Pencil,
    Search,
    Trash2,
    Users,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { formatDocument } from '../../common/formatters';
import StatusChip from '../../component/common/StatusChip';
import NewItemButton from '../../component/common/NewItemButton';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileCustomersList from '../../component/customer/MobileCustomersList';
import { protoIcon } from '../../component/common/ProtoIcon';
import { useAppBarAction } from '../../component/layout/AppBarActions';

const UploadIcon = protoIcon('upload');
const DownloadIcon = protoIcon('download');
const MOBILE_PAGE_SIZE = 20;

const PAGE_SIZE_OPTIONS = [5, 8, 15];

// Filtro aplicado só ao clicar "Buscar"/Enter — mesma técnica de
// `sales/List.js` (report-toolbar §6.1): o estado do campo (`typeFilter`/
// `search`) é distinto do filtro efetivamente aplicado (`appliedType`/
// `appliedSearch`), que alimenta os chips removíveis e a busca no servidor.
export default function Customers() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação; página própria para não
    // interferir na paginação do desktop/tablet.
    const [mobilePage, setMobilePage] = useState(0);

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    // Paginação server-side: `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPJ, setTotalPJ] = useState(0);
    const [totalPF, setTotalPF] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);

    // Valores dos campos do formulário de filtro (não aplicados ainda).
    const [typeFilter, setTypeFilter] = useState('');
    const [search, setSearch] = useState('');
    // Filtros efetivamente aplicados — alimentam os chips removíveis.
    const [appliedType, setAppliedType] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Busca a página atual no servidor sempre que filtros aplicados/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedType) params.type = appliedType;
        if (appliedSearch.trim()) params.search = appliedSearch.trim();

        setLoading(true);
        httpClient.get('/customers', { params })
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.items || [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && rows.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setCustomers((prev) => [...prev, ...rows.filter((row) => !prev.some((p) => p.ID === row.ID))]);
                } else {
                    setCustomers(rows);
                }
                setTotalCount(total);
                setTotalPJ(response.data?.total_pj || 0);
                setTotalPF(response.data?.total_pf || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching customers:', error);
                setCustomers([]);
                setTotalCount(0);
                setTotalPJ(0);
                setTotalPF(0);
                setSnackbarMessage('Erro ao buscar clientes');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedType, appliedSearch, reloadKey, isMobileShell, mobilePage]);

    // Mobile: a busca é aplicada sozinha (debounce), sem botão "Buscar".
    useEffect(() => {
        if (!isMobileShell || search.trim() === appliedSearch) return undefined;
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
            setMobilePage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [search, appliedSearch, isMobileShell]);

    const handleApplyFilters = () => {
        setAppliedType(typeFilter);
        setAppliedSearch(search);
        setPage(0);
        setMobilePage(0);
    };

    const handleClearFilters = () => {
        setTypeFilter('');
        setSearch('');
        setAppliedType('');
        setAppliedSearch('');
        setPage(0);
        setMobilePage(0);
    };

    // Mobile: chips/folha de filtros aplicam o tipo na hora.
    const handleMobileTypeChange = (value) => {
        setTypeFilter(value);
        setAppliedType(value);
        setMobilePage(0);
    };

    const handleRemoveTypeFilter = () => {
        setTypeFilter('');
        setAppliedType('');
        setPage(0);
    };

    const handleRemoveSearchFilter = () => {
        setSearch('');
        setAppliedSearch('');
        setPage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const handleDelete = async () => {
        const response = await httpClient.delete(`/customers/${customerToDelete}`);
        if (response.status === 204) {
            setOpenDialogConfirmDelete(false);
            setReloadKey((k) => k + 1);
            setSnackbarMessage('Cliente excluído com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        }
    };

    const handleExport = async () => {
        try {
            await httpClient.downloadFile('/customers/export', {
                title: 'clientes',
                extension: 'csv',
                responseType: 'blob'
            });
        } catch (error) {
            console.error('Error exporting customers:', error);
            setSnackbarMessage('Erro ao exportar clientes. Tente novamente.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    useAppBarAction({
        enabled: isMobileShell,
        menu: [
            ...(hasPermission('customer.import') ? [{ label: 'Importar', icon: UploadIcon, onClick: () => navigate('/customers/import') }] : []),
            ...(hasPermission('customer.export') ? [{ label: 'Exportar', icon: DownloadIcon, onClick: handleExport }] : []),
        ],
    });

    const columns = [
        {
            path: 'Name',
            name: 'Nome',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {row.Name}
                    </Typography>
                   
                </Box>
            ),
        },
        {
            path: 'Document',
            name: 'Documento',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {formatDocument(row.Document) || '—'}
                    </Typography>
                    
                </Box>
            ),
        },
        {
            path: 'Cellphone',
            name: 'Celular',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: row.Cellphone ? 'text.secondary' : 'text.disabled' }}>
                    {row.Cellphone || '—'}
                </Typography>
            ),
        },
        {
            path: 'City',
            name: 'Cidade',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: row.City ? 'text.secondary' : 'text.disabled' }}>
                    {row.City || '—'}
                </Typography>
            ),
        },
    ];

    const actions = [
        ...(hasPermission('customer.details') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => navigate(`/customers/view/${row.ID}`),
                tooltip: 'Visualizar',
                style: { cursor: 'pointer' }
            }
        }] : []),
        ...(hasPermission('customer.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => navigate(`/customers/edit/${row.ID}`),
                tooltip: 'Editar',
                style: { cursor: 'pointer' }
            }
        }] : []),
        ...(hasPermission('customer.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    setCustomerToDelete(row.ID);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Excluir',
                style: { cursor: 'pointer' }
            }
        }] : []),
    ];

    // Shell mobile: lista do protótipo `clientes/lista.html` (mesmos dados, filtros e navegação).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Clientes"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileCustomersList
                    customers={customers}
                    loading={loading}
                    totalCount={totalCount}
                    hasMore={customers.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    type={appliedType}
                    onTypeChange={handleMobileTypeChange}
                    search={search}
                    onSearchChange={setSearch}
                    onClearFilters={handleClearFilters}
                    canCreate={hasPermission('customer.create')}
                    onNew={() => navigate('/customers/new')}
                    onOpen={hasPermission('customer.details') ? (id) => navigate(`/customers/view/${id}`) : undefined}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Clientes"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ações (Importar/Exportar/Novo cliente). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Users size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Clientes
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Cadastre e consulte os clientes pessoa física e pessoa jurídica.
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1}>
                        {hasPermission('customer.import') && (
                            <Button
                                variant="outlined"
                                startIcon={<FileUp size={15} strokeWidth={2} />}
                                onClick={() => navigate('/customers/import')}
                            >
                                Importar
                            </Button>
                        )}
                        {hasPermission('customer.export') && (
                            <Button
                                variant="outlined"
                                startIcon={<Download size={15} strokeWidth={2} />}
                                onClick={handleExport}
                            >
                                Exportar
                            </Button>
                        )}
                        {hasPermission('customer.create') && (
                            <NewItemButton label="Novo cliente" onClick={() => navigate('/customers/new')} />
                        )}
                    </Stack>
                </Box>

                {/* report-toolbar — Tipo + Buscar, em card separado. */}
                <FilterToolbar
                    plain
                    activeCount={typeFilter ? 1 : 0}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 260px' }}>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Buscar
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome, documento ou celular…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Tipo
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select
                                    value={typeFilter}
                                    displayEmpty
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    renderValue={(value) => (value === 'pj' ? 'Pessoa Jurídica' : value === 'pf' ? 'Pessoa Física' : 'Todos')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                                    <MenuItem value="pf">Pessoa Física</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* chips-row — filtros ativos removíveis + contagem/quebra PJ/PF. */}
                <FilterChips>
                    {appliedType && (
                        <Chip
                            label={`Tipo: ${appliedType === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}`}
                            size="small"
                            onDelete={handleRemoveTypeFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleRemoveSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} cliente${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`${totalPJ} Pessoa Jurídica / ${totalPF} Pessoa Física`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={customers}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={hasPermission('customer.details') ? (row) => navigate(`/customers/view/${row.ID}`) : undefined}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    itemLabel="clientes"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            <DialogForm
                iconType="warning"
                open={openDialogConfirmDelete}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                content="Tem certeza que deseja excluir o cliente?"
                secondaryContent="Esta ação não pode ser desfeita. O cliente será removido permanentemente do sistema."
                onSubmit={handleDelete}
                onClose={() => setOpenDialogConfirmDelete(false)}
            />
        </PageScaffold>
    );
}
